import React, { useState, useEffect } from 'react';
import { change, Field, reduxForm } from 'redux-form';
import { Col, Row, Card, CardBody } from 'reactstrap';
import PropTypes from 'prop-types';
import Tooltip from '@mui/material/Tooltip';
import Fab from '@mui/material/Fab';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import PersonIcon from '@mui/icons-material/Person';
import BadgeIcon from '@mui/icons-material/Badge';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import DomainIcon from '@mui/icons-material/Domain';
import PublicIcon from '@mui/icons-material/Public';
import MapIcon from '@mui/icons-material/Map';
import LayersIcon from '@mui/icons-material/Layers';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import MarkAsUnreadIcon from '@mui/icons-material/MarkAsUnread';
import SmartphoneIcon from '@mui/icons-material/Smartphone';

import renderSelectField from 'src/shared/components/form/Select';
import renderField from 'src/shared/components/form/renderField';
import PndRestApi from 'src/utils/pnd/PndRestApi';
import { showNotification } from 'src/shared/components/Notification';
import { connect, useSelector } from 'react-redux';
import MerchantCustomerAddNewAddressModal from './MerchantCustomerAddNewAddressModal';
import MerchantCustomerAddressChangeModal from './MerchantCustomerAddressChangeModal';
import { ERROR_MESSAGES } from 'src/utils/pnd/PndConstants';

const required = value => (value ? undefined : 'Required');

const MerchantCustomerInformation = ({ dispatch, isCustomersId }) => {
    const [isPopUpCustomer, setPopUpCustomer] = useState(false);
    const [isMerchantCustomerAddress, setMerchantCustomerAddress] = useState(false);
    const [merCustomerId, setMerCustomerId] = useState(null);

    const formCustomer = useSelector(state => state.form?.orderForm?.values?.name);

    useEffect(() => {
        if (formCustomer?.value && !merCustomerId) {
            setMerCustomerId(formCustomer.value);
        }
    }, [formCustomer, merCustomerId]);

    const getMerchantCustomersInfoById = async (event) => {
        try {
            setMerCustomerId(event?.value);
            const response = await PndRestApi.GetMerchantCustomerById(localStorage.getItem('mi'), event?.value);
            if (response.status === 200 && response.data.status === 200 && response.data.data.length > 0) {
                const data = response.data.data[0];
                dispatch(change('orderForm', 'shortCode', data.merchantCustomerCode));
                dispatch(change('orderForm', 'address', data.address));
                dispatch(change('orderForm', 'buildingName', data.buildingName));
                dispatch(change('orderForm', 'contactNo', data.mobileNo));
                dispatch(change('orderForm', 'postalCode', data.poBoxNo));
                dispatch(change('orderForm', 'doorNo', data.doorNo));
                dispatch(change('orderForm', 'floorNo', data.floorNo));
                if (data.contactName) {
                    dispatch(change('orderForm', 'contactPersonName', data.contactName));
                }
                dispatch(change('orderForm', 'emiratesName', data.emirates));
                dispatch(change('orderForm', 'grlCode', data.grl));
                if (data.area) dispatch(change('orderForm', 'areaName', data.area));
                if (data.longitude) dispatch(change('orderForm', 'longitude', data.longitude));
                if (data.latitude) dispatch(change('orderForm', 'latitude', data.latitude));
            } else {
                ['shortCode', 'address', 'buildingName', 'contactNo', 'postalCode', 'doorNo', 'floorNo', 'emiratesName', 'areaName', 'contactPersonName', 'grlCode'].forEach(field => {
                    dispatch(change('orderForm', field, ""));
                });
                showNotification("theme-light", "ltr", `SuccessCode: ${response.data.status}`, `Message:${response.data.message}`);
            }
        } catch (error) {
            console.error('Error fetching data:', error);
            showNotification("theme-light", "ltr", "SuccessCode: 500", ERROR_MESSAGES.tryCatchError);
        }
    };

    return (
        <Card className="border-0 shadow-sm mb-3">
            <CardBody className="p-3">
                <div className="d-flex align-items-center mb-3 text-primary border-bottom pb-2">
                    <PersonIcon style={{ fontSize: '1.2rem' }} className="me-2" />
                    <h6 className="m-0 fw-bold small text-uppercase">Customer Information</h6>
                </div>
                <Row className="g-2 mb-2">
                    <Col lg={4} md={6}>
                        <div className="mb-1">
                            <label className="form-label fw-bold text-muted small mb-0" style={{ fontSize: '0.75rem' }}>Customer Name <span className='text-danger'>*</span></label>
                            <div className="d-flex align-items-center">
                                <div className="flex-grow-1">
                                    <Field name="name" component={renderSelectField} options={isCustomersId} placeholder="Select Customer" onChange={getMerchantCustomersInfoById} validate={[required]} />
                                </div>
                                <Tooltip title="Add New Customer">
                                    <Fab color="success" size="small" className="ms-1" style={{ width: '30px', height: '30px', minHeight: '30px' }} onClick={() => setPopUpCustomer(true)}>
                                        <AddIcon style={{ fontSize: '1rem' }} />
                                    </Fab>
                                </Tooltip>
                            </div>
                        </div>
                    </Col>
                    <Col lg={2} md={6}>
                        <div className="mb-1">
                            <label className="form-label fw-bold text-muted small mb-0" style={{ fontSize: '0.75rem' }}>Customer Code</label>
                            <Field name="shortCode" component={renderField} type="text" placeholder="Code" icon={BadgeIcon} disabled={true} />
                        </div>
                    </Col>
                    <Col lg={6} md={12}>
                        <div className="mb-1">
                            <label className="form-label fw-bold text-muted small mb-0" style={{ fontSize: '0.75rem' }}>Full Address <span className='text-danger'>*</span></label>
                            <div className="d-flex align-items-center">
                                <div className="flex-grow-1">
                                    <Field name="address" component={renderField} type="textarea" placeholder="Address" validate={[required]} icon={LocationOnIcon} />
                                </div>
                                <Tooltip title="Change Address">
                                    <Fab color="success" size="small" className="ms-1" style={{ width: '30px', height: '30px', minHeight: '30px' }} onClick={() => setMerchantCustomerAddress(true)}>
                                        <EditIcon style={{ fontSize: '1rem' }} />
                                    </Fab>
                                </Tooltip>
                            </div>
                        </div>
                    </Col>
                </Row>
                <Row className="g-2 mb-2">
                    <Col lg={4} md={6}>
                        <div className="mb-1">
                            <label className="form-label fw-bold text-muted small mb-0" style={{ fontSize: '0.75rem' }}>Building Name <span className='text-danger'>*</span></label>
                            <Field name="buildingName" component={renderField} type="text" placeholder="Building Name" validate={[required]} icon={DomainIcon} />
                        </div>
                    </Col>
                    <Col lg={2} md={6}>
                        <div className="mb-1">
                            <label className="form-label fw-bold text-muted small mb-0" style={{ fontSize: '0.75rem' }}>Emirate</label>
                            <Field name="emiratesName" component={renderField} type="text" placeholder="Emirate" validate={[required]} icon={PublicIcon} />
                        </div>
                    </Col>
                    <Col lg={2} md={6}>
                        <div className="mb-1">
                            <label className="form-label fw-bold text-muted small mb-0" style={{ fontSize: '0.75rem' }}>Area</label>
                            <Field name="areaName" component={renderField} type="text" placeholder="Area" validate={[required]} icon={MapIcon} />
                        </div>
                    </Col>
                    <Col lg={2} md={6}>
                        <div className="mb-1">
                            <label className="form-label fw-bold text-muted small mb-0" style={{ fontSize: '0.75rem' }}>GRL</label>
                            <Field name="grlCode" component={renderField} type="text" placeholder="GRL" disabled={true} icon={MarkAsUnreadIcon} />
                        </div>
                    </Col>
                    <Col lg={2} md={6}>
                        <div className="mb-1">
                            <label className="form-label fw-bold text-muted small mb-0" style={{ fontSize: '0.75rem' }}>Floor Number</label>
                            <Field name="floorNo" component={renderField} type="text" placeholder="Floor" icon={LayersIcon} />
                        </div>
                    </Col>
                </Row>
                <Row className="g-2">
                    <Col lg={3} md={6}>
                        <div className="mb-1">
                            <label className="form-label fw-bold text-muted small mb-0" style={{ fontSize: '0.75rem' }}>Door Number</label>
                            <Field name="doorNo" component={renderField} type="text" placeholder="Door No." icon={MeetingRoomIcon} />
                        </div>
                    </Col>
                    <Col lg={3} md={6}>
                        <div className="mb-1">
                            <label className="form-label fw-bold text-muted small mb-0" style={{ fontSize: '0.75rem' }}>Postal Code</label>
                            <Field name="postalCode" component={renderField} type="text" placeholder="Postal Code" icon={MarkAsUnreadIcon} />
                        </div>
                    </Col>
                    <Col lg={3} md={6}>
                        <div className="mb-1">
                            <label className="form-label fw-bold text-muted small mb-0" style={{ fontSize: '0.75rem' }}>Contact Name</label>
                            <Field name="contactPersonName" component={renderField} type="text" placeholder="Contact Person" icon={PersonIcon} />
                        </div>
                    </Col>
                    <Col lg={3} md={6}>
                        <div className="mb-1">
                            <label className="form-label fw-bold text-muted small mb-0" style={{ fontSize: '0.75rem' }}>Mobile Number <span className='text-danger'>*</span></label>
                            <Field name="contactNo" component={renderField} type="text" placeholder="971xxxxxxx" validate={[required]} icon={SmartphoneIcon} />
                        </div>
                    </Col>
                </Row>
                {isMerchantCustomerAddress && merCustomerId && (
                    <MerchantCustomerAddressChangeModal modalShow={isMerchantCustomerAddress} setModalShow={setMerchantCustomerAddress} isTitle={"Change Customer Address"} merCustomerId={merCustomerId} />
                )}
                {isPopUpCustomer && (
                    <MerchantCustomerAddNewAddressModal modalShow={isPopUpCustomer} setModalShow={setPopUpCustomer} isTitle={"Add New Customer Address"} />
                )}
            </CardBody>
        </Card>
    );
};

MerchantCustomerInformation.propTypes = {
    dispatch: PropTypes.func,
    isCustomersId: PropTypes.array,
};

export default connect()(reduxForm({
    form: "orderForm",
    enableReinitialize: true,
    destroyOnUnmount: false,
    forceUnregisterOnUnmount: false,
})(MerchantCustomerInformation));
